/**
 * Checks on behavior data that arrives as serialized input, such as a tool
 * call, before an authoring operation writes it. They are stricter than a
 * load, which stores a document as it is: new data must be data the runtime
 * can run.
 */

import type { PropertiesData } from '../../scenedata/behaviors/propertiesdata';
import type { BehaviorGraphData } from '../../scenedata/behaviors/behaviorgraphdata';
import { lowerBehaviorGraphData } from '../../behaviors/behaviorgraph/macros/lower';
import { assertExecutableGraph } from '../../behaviors/behaviorgraph/graphValidation';

/**
 * Throw unless `value` is a literal of the declared property type. A
 * declaration holds a value, never an expression: a binding belongs on the
 * field that consumes the property.
 */
export function validatePropertyValue(type: string, value: unknown): void {
    const vector = (length: number) => Array.isArray(value) && value.length === length && value.every(n => typeof n === 'number' && Number.isFinite(n));
    const valid = type === 'number' ? typeof value === 'number' && Number.isFinite(value)
        : type === 'boolean' ? typeof value === 'boolean'
        : type === 'string' ? typeof value === 'string'
        : type === 'vec3' ? vector(3)
        : type === 'vec4' ? vector(4)
        : type === 'color' ? (typeof value === 'string' && /^#(?:[\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})$/i.test(value)) || vector(3) || vector(4)
        : false;
    if (!valid) throw new Error(`value must match declared ${type} property (numbers must be finite)`);
}

/**
 * Throw unless the active graph of `data` can run once its macros are
 * lowered. Never mutates `data`.
 */
export function assertAuthoredGraph(data: BehaviorGraphData): void {
    const lowered = lowerBehaviorGraphData(structuredClone(data));
    const graphs = lowered.graphs ?? (lowered.graph ? [lowered.graph] : []);
    if (!graphs.length) return;
    const active = lowered.activeGraph ?? 0;
    if (!Number.isInteger(active) || !graphs[active]) throw new Error(`Invalid activeGraph index ${active}`);
    assertExecutableGraph(graphs[active], graphs);
}

// Exhaustive against the format: a field added to `PropertiesData` must be
// listed here, or every write from serialized input would strip it.
const propertiesKeys = {
    '@type': true, properties: true, transform: true, states: true,
    initialState: true, sheets: true, stateGroup: true,
} satisfies Record<keyof PropertiesData, true>;
const PROPERTIES_KEYS = new Set(Object.keys(propertiesKeys));

/**
 * Fit Properties data from serialized input to the format, in place: drop
 * top-level keys `PropertiesData` doesn't have and states that aren't
 * objects, and give each state its `values`. Returns a sentence for each
 * thing dropped.
 *
 * Throws, before changing anything, when a declaration's value is not a
 * literal of its type (see {@link validatePropertyValue}).
 */
export function sanitizePropertiesData(data: Record<string, unknown>): string[] {
    if (Array.isArray(data.properties)) for (const property of data.properties) {
        if (!property || typeof property !== 'object' || !('value' in property)) continue;
        const declaration = property as Record<string, unknown>;
        try { validatePropertyValue(String(declaration.type), declaration.value); }
        catch (error) {
            throw new TypeError(`Property "${String(declaration.name)}": ${String(error)}. Defaults are literal values; put bindings on consuming fields or use computed graph assignments/guards.`);
        }
    }

    const warnings: string[] = [];
    const unknownKeys = Object.keys(data).filter(key => !PROPERTIES_KEYS.has(key));
    for (const key of unknownKeys) delete data[key];
    if (unknownKeys.length > 0) {
        warnings.push(`Stripped unknown top-level keys: ${unknownKeys.join(', ')}. Properties only accepts: ${[...PROPERTIES_KEYS].filter(key => key !== '@type').join(', ')}.`);
    }

    if (!data.states || typeof data.states !== 'object') return warnings;
    const states = data.states as Record<string, unknown>;
    for (const [name, state] of Object.entries(states)) {
        if (state == null || typeof state !== 'object') {
            warnings.push(`Stripped state "${name}" — value was ${JSON.stringify(state)}, expected an object like { values: {}, transform?: {}, transition?: {} }.`);
            delete states[name];
        } else if (!('values' in state)) {
            (state as Record<string, unknown>).values = {};
        }
    }
    return warnings;
}
